import { useEffect, useMemo, useRef } from 'react'

import { useSetTreasuryPasskey } from '~/components/create-team/useSetTreasuryPasskey'
import { useActiveOrgSmartAccountsSetup } from '~/hooks/org'
import { useRequiredAuthUser } from '~/hooks/useAuthUser'
import { AuthOrg, OrgPasskey } from '~/types'

/**
 * If the user has at least one passkey and no preferred passkey, set one.
 */
export const useSetFirstPasskey = (org: AuthOrg) => {
  const { setTreasuryPasskey } = useSetTreasuryPasskey()
  const user = useRequiredAuthUser()
  const setup = useActiveOrgSmartAccountsSetup()

  const userPasskeys = useMemo(
    () => org.passkeys.filter((p) => p.user?.id === user.id) as OrgPasskey[],
    [org.passkeys, user.id],
  )

  const setupPasskeys = useMemo(
    () => setup?.authenticators?.filter((a) => a.setupAccount === 'TREASURY'),
    [setup?.authenticators],
  )

  const defaultTreasuryPasskeyId = userPasskeys.find((p) =>
    setupPasskeys?.find((sp) => sp.authenticatorId === p.id),
  )?.id

  const firstPasskeyId = userPasskeys[0]?.id

  const hasSetDefaultPasskeyRef = useRef(false)

  useEffect(() => {
    if (
      user?.id &&
      firstPasskeyId &&
      !defaultTreasuryPasskeyId &&
      !hasSetDefaultPasskeyRef.current
    ) {
      // Set the ref immediately to prevent duplicate calls
      hasSetDefaultPasskeyRef.current = true
      setTreasuryPasskey(firstPasskeyId, () => {
        hasSetDefaultPasskeyRef.current = false
      })
    }
  }, [defaultTreasuryPasskeyId, firstPasskeyId, setTreasuryPasskey, user?.id])
}
